/**
 * Authentication Service
 *
 * Handles OAuth login flows and JWT token management
 * Supports both authenticated users (OAuth + JWT) and guest users (localStorage only)
 */

// Use current domain origin in production, localhost in development
const API_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3001');

export const authService = {
  // ============================================
  // OAuth Login Flows
  // ============================================

  /**
   * Initiate Google OAuth login
   * Redirects to backend OAuth endpoint
   */
  loginWithGoogle: () => {
    window.location.href = `${API_URL}/auth/google`;
  },

  /**
   * Initiate Discord OAuth login
   * Redirects to backend OAuth endpoint
   */
  loginWithDiscord: () => {
    window.location.href = `${API_URL}/auth/discord`;
  },

  // ============================================
  // OAuth Callback Handling
  // ============================================

  /**
   * Handle OAuth callback
   * Extracts JWT token from URL query parameters
   * @returns {Object} { success: boolean, user?: Object, error?: string }
   */
  handleCallback: () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const error = params.get('error');

    if (error) {
      console.error('[Auth] OAuth error:', error);
      return { success: false, error };
    }

    if (token) {
      try {
        // Store JWT token
        localStorage.setItem('jwt_token', token);

        // Decode JWT to get user info (no verification needed - backend already verified)
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Store user data
        const user = {
          id: payload.userId,
          email: payload.email,
          username: payload.username,
          usernameConfirmed: payload.usernameConfirmed || false,
          provider: payload.provider,
          createdAt: payload.createdAt,
        };

        localStorage.setItem('user', JSON.stringify(user));

        // Also set old system tokens for backward compatibility
        localStorage.setItem('gameToken', token);
        localStorage.setItem('username', user.username);
        localStorage.setItem('tokenCreated', new Date().toISOString());

        console.log('[Auth] OAuth login successful:', user.email);
        return { success: true, user };
      } catch (err) {
        console.error('[Auth] Error processing token:', err);
        return { success: false, error: 'Failed to process authentication token' };
      }
    }

    return { success: false, error: 'No token received' };
  },

  // ============================================
  // Token Management
  // ============================================

  /**
   * Get JWT token
   * @returns {string|null} JWT token
   */
  getToken: () => {
    return localStorage.getItem('jwt_token');
  },

  /**
   * Get current user data
   * @returns {Object|null} User object
   */
  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Check if user is authenticated (OAuth or guest)
   * @returns {boolean} True if authenticated
   */
  isAuthenticated: () => {
    // Check for OAuth JWT token
    const token = authService.getToken();
    if (token) {
      try {
        // Check if token is expired
        const payload = JSON.parse(atob(token.split('.')[1]));
        const isExpired = payload.exp * 1000 < Date.now();

        if (isExpired) {
          console.warn('[Auth] Token expired, clearing auth data');
          authService.logout();
          return false;
        }

        return true;
      } catch (err) {
        console.error('[Auth] Error checking token:', err);
        return false;
      }
    }

    // Check for guest session
    const hasGameToken = !!localStorage.getItem('gameToken');
    const hasUsername = !!localStorage.getItem('username');
    return hasGameToken && hasUsername;
  },

  /**
   * Check if using guest mode (old localStorage system)
   * @returns {boolean} True if guest
   */
  isGuest: () => {
    const hasJWT = !!authService.getToken();
    const hasOldToken = !!localStorage.getItem('gameToken');
    return !hasJWT && hasOldToken;
  },

  // ============================================
  // User Actions
  // ============================================

  /**
   * Logout user
   * Clears all authentication data
   */
  logout: () => {
    // Clear JWT auth
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');

    // Clear old system tokens
    localStorage.removeItem('gameToken');
    localStorage.removeItem('username');
    localStorage.removeItem('tokenCreated');

    // Clear room data
    localStorage.removeItem('currentRoom');
    localStorage.removeItem('isHost');
    localStorage.removeItem('gameState');
    localStorage.removeItem('intendedDestination');

    console.log('[Auth] User logged out');
  },

  /**
   * Verify token with backend
   * Optional check to ensure token is still valid server-side
   * @returns {Promise<boolean>} True if valid
   */
  verifyToken: async () => {
    const token = authService.getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn('[Auth] Token verification failed');
        authService.logout();
        return false;
      }

      return true;
    } catch (err) {
      console.error('[Auth] Error verifying token:', err);
      return false;
    }
  },

  /**
   * Get current user profile from backend
   * @returns {Promise<Object|null>} User profile or null
   */
  getUserProfile: async () => {
    const token = authService.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.user;
    } catch (err) {
      console.error('[Auth] Error getting user profile:', err);
      return null;
    }
  },

  // ============================================
  // Guest Mode Support
  // ============================================

  /**
   * Create guest session (old system compatibility)
   * @param {string} username - Guest username (optional, defaults to placeholder)
   * @param {boolean} usernameConfirmed - Whether username has been confirmed
   * @returns {boolean} Success
   */
  createGuestSession: (username = 'Guest', usernameConfirmed = false) => {
    try {
      // Generate a simple token for guest
      const token = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create guest user object similar to OAuth users
      const guestUser = {
        username,
        usernameConfirmed,
        isGuest: true,
      };

      localStorage.setItem('gameToken', token);
      localStorage.setItem('username', username);
      localStorage.setItem('user', JSON.stringify(guestUser));
      localStorage.setItem('tokenCreated', new Date().toISOString());

      console.log('[Auth] Guest session created:', username, 'confirmed:', usernameConfirmed);
      return true;
    } catch (err) {
      console.error('[Auth] Error creating guest session:', err);
      return false;
    }
  },

  /**
   * Update guest username
   * @param {string} username - New username
   * @returns {boolean} Success
   */
  updateGuestUsername: (username) => {
    try {
      // Update user object
      const user = authService.getUser() || {};
      user.username = username;
      user.usernameConfirmed = true;

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('username', username);

      console.log('[Auth] Guest username updated:', username);
      return true;
    } catch (err) {
      console.error('[Auth] Error updating guest username:', err);
      return false;
    }
  },

  /**
   * Get username (works for both authenticated and guest users)
   * @returns {string|null} Username
   */
  getUsername: () => {
    // Try JWT user first
    const user = authService.getUser();
    if (user) return user.username;

    // Fall back to old system
    return localStorage.getItem('username');
  },
};

export default authService;
